import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import PhotoUpload from "../../components/PhotoUpload";
import LocationPicker from "../../components/LocationPicker";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { getStudent, createStudent, updateStudent, uploadStudentPhoto } from "../../api/studentApi";
import { cleanText } from "../../utils/textUtils";
import "./Students.css";

const academicYearApi = createLookupApi("academic-years");
const groupApi = createLookupApi("groups");
const categoryApi = createLookupApi("student-categories");

/** Real fixed option list, direct port of both `student-single-profile-all-update.html`'s own `<select id="fatherOccupation">` and `<select id="motherOccupation">` (identical except Mother's own extra "House Wife" option). */
const FATHER_OCCUPATIONS = [
  "Business", "Service", "Private Service", "Govt. Service", "Teacher", "Banker", "Doctor", "Engineer", "Lawyer",
  "Journalist", "Farmer", "Driver", "Phycian", "Student", "Army", "Police Officer", "Navy", "Airforce",
  "Retired Person", "NRB", "BGB", "N/A", "NSI",
];
const MOTHER_OCCUPATIONS = ["House Wife", ...FATHER_OCCUPATIONS];

/** Real fixed option list, direct port of the same template's `<select id="quota">`. */
const QUOTA_OPTIONS = ["BGB", "Army", "Police", "Freedom Fighter", "Not Applicable", "Tribes"];

/** Real fixed option list, direct port of the same template's `<select id="autism">`. */
const AUTISM_OPTIONS = ["No", "Autistic", "Physically"];

const emptyForm = {
  customStudentId: "",
  studentName: "",
  studentNameBangla: "",
  studentNickName: "",
  studentGender: "Male",
  studentDob: "",
  studentReligion: "Islam",
  birthCertificateNo: "",
  quota: "",
  autism: "No",
  fatherName: "",
  fatherNameBangla: "",
  fatherOccupation: "",
  fatherDesignation: "",
  fatherWorkingPlace: "",
  fatherMonthlyIncome: "",
  fatherNid: "",
  fatherEducation: "",
  fatherMobile: "",
  motherName: "",
  motherNameBangla: "",
  motherOccupation: "",
  motherDesignation: "",
  motherWorkingPlace: "",
  motherMonthlyIncome: "",
  motherNid: "",
  motherEducation: "",
  motherMobile: "",
  guardianMobile: "",
  guardianEmail: "",
  bloodGroup: "",
  mailingAddress: "",
  presentPostOffice: "",
  presentVillage: "",
  permanentPostOffice: "",
  permanentVillage: "",
  registrationNo: "",
  // Previous institute (transfer-in) — direct port of the real "Previous Institution" section.
  previousInstituteName: "",
  previousInstituteCode: "",
  previousInstituteAddress: "",
  previousInstituteMobile: "",
  previousInstituteEmail: "",
  previousInstituteExamName: "",
  previousInstituteBoardName: "",
  previousInstituteBoardRoll: "",
  previousInstituteRegNo: "",
  previousInstituteGpa: "",
  previousInstituteGrade: "",
  previousInstitutePassingYear: "",
  previousInstituteSession: "",
  previousInstituteTimePeriod: "",
  // enrollment (create only)
  academicYear: "",
  classConfigurationId: "",
  groupId: "",
  categoryId: "",
  studentRoll: "",
};

export default function StudentFormPage() {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(emptyForm);
  const [presentLocation, setPresentLocation] = useState({});
  const [permanentLocation, setPermanentLocation] = useState({});
  const [imageName, setImageName] = useState(null);
  const [academicYears, setAcademicYears] = useState([]);
  const [classConfigurations, setClassConfigurations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const lookups = Promise.all([academicYearApi.list(), listClassConfigurations(), groupApi.list(), categoryApi.list()]);

    const studentLoad = isEdit ? getStudent(id) : Promise.resolve(null);

    Promise.all([lookups, studentLoad])
      .then(([[years, configs, groupList, categoryList], student]) => {
        setAcademicYears(years);
        setClassConfigurations(configs);
        setGroups(groupList);
        setCategories(categoryList);

        if (student) {
          setForm({
            ...emptyForm,
            customStudentId: student.customStudentId || "",
            studentName: student.studentName || "",
            studentNameBangla: student.studentNameBangla || "",
            studentNickName: student.studentNickName || "",
            studentGender: student.studentGender || "Male",
            studentDob: student.studentDob || "",
            studentReligion: student.studentReligion || "",
            birthCertificateNo: student.birthCertificateNo || "",
            quota: student.quota || "",
            autism: student.autism || "No",
            fatherName: student.fatherName || "",
            fatherNameBangla: student.fatherNameBangla || "",
            fatherOccupation: student.fatherOccupation || "",
            fatherDesignation: student.fatherDesignation || "",
            fatherWorkingPlace: student.fatherWorkingPlace || "",
            fatherMonthlyIncome: student.fatherMonthlyIncome || "",
            fatherNid: student.fatherNid || "",
            fatherEducation: student.fatherEducation || "",
            fatherMobile: student.fatherMobile || "",
            motherName: student.motherName || "",
            motherNameBangla: student.motherNameBangla || "",
            motherOccupation: student.motherOccupation || "",
            motherDesignation: student.motherDesignation || "",
            motherWorkingPlace: student.motherWorkingPlace || "",
            motherMonthlyIncome: student.motherMonthlyIncome || "",
            motherNid: student.motherNid || "",
            motherEducation: student.motherEducation || "",
            motherMobile: student.motherMobile || "",
            guardianMobile: student.guardianMobile || "",
            guardianEmail: student.guardianEmail || "",
            bloodGroup: student.bloodGroup || "",
            mailingAddress: student.mailingAddress || "",
            presentPostOffice: student.presentPostOffice || "",
            presentVillage: student.presentVillage || "",
            permanentPostOffice: student.permanentPostOffice || "",
            permanentVillage: student.permanentVillage || "",
            registrationNo: cleanText(student.registrationNo),
            previousInstituteName: student.previousInstituteName || "",
            previousInstituteCode: student.previousInstituteCode || "",
            previousInstituteAddress: student.previousInstituteAddress || "",
            previousInstituteMobile: student.previousInstituteMobile || "",
            previousInstituteEmail: student.previousInstituteEmail || "",
            previousInstituteExamName: student.previousInstituteExamName || "",
            previousInstituteBoardName: student.previousInstituteBoardName || "",
            previousInstituteBoardRoll: student.previousInstituteBoardRoll || "",
            previousInstituteRegNo: student.previousInstituteRegNo || "",
            previousInstituteGpa: student.previousInstituteGpa != null ? String(student.previousInstituteGpa) : "",
            previousInstituteGrade: student.previousInstituteGrade || "",
            previousInstitutePassingYear: student.previousInstitutePassingYear || "",
            previousInstituteSession: student.previousInstituteSession || "",
            previousInstituteTimePeriod: student.previousInstituteTimePeriod || "",
          });
          setPresentLocation({ thanaId: student.presentThanaId });
          setPermanentLocation({ thanaId: student.permanentThanaId });
          setImageName(student.imageName);
        } else if (years.length > 0) {
          setForm((f) => ({ ...f, academicYear: years[0].name }));
        }
      })
      .catch(() => setError("Failed to load form data."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    const previousInstituteGpa = form.previousInstituteGpa !== "" ? Number(form.previousInstituteGpa) : null;
    try {
      if (isEdit) {
        await updateStudent(id, {
          ...form,
          previousInstituteGpa,
          presentThanaId: presentLocation.thanaId || null,
          permanentThanaId: permanentLocation.thanaId || null,
        });
        setSuccess("Student updated.");
      } else {
        const created = await createStudent({
          ...form,
          previousInstituteGpa,
          classConfigurationId: Number(form.classConfigurationId),
          groupId: Number(form.groupId),
          categoryId: Number(form.categoryId),
          studentRoll: form.studentRoll ? Number(form.studentRoll) : null,
          presentThanaId: presentLocation.thanaId || null,
          permanentThanaId: permanentLocation.thanaId || null,
          customStudentId: form.customStudentId || undefined,
        });
        setSuccess("Student registered successfully.");
        history.push(`/students/${created.studentId}/edit`);
      }
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    const updated = await uploadStudentPhoto(id, file);
    setImageName(updated.imageName);
  };

  if (loading) {
    return (
      <MainLayout>
        <p>Loading...</p>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{isEdit ? "Edit Student" : "Register Student"}</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <form onSubmit={handleSubmit} className="student-form-page">
        {isEdit && (
          <div className="form-section">
            <h2>Photo</h2>
            <PhotoUpload folder="students" fileName={imageName} onUpload={handlePhotoUpload} />
          </div>
        )}

        {!isEdit && (
          <div className="form-section">
            <h2>Enrollment</h2>
            <div className="form-grid">
              <label>
                Academic Year *
                <select value={form.academicYear} onChange={set("academicYear")} required>
                  <option value="" disabled>
                    Select year
                  </option>
                  {academicYears.map((y) => (
                    <option key={y.id} value={y.name}>
                      {y.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Class *
                <select value={form.classConfigurationId} onChange={set("classConfigurationId")} required>
                  <option value="" disabled>
                    Select class
                  </option>
                  {classConfigurations.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.classConfigName}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Group *
                <select value={form.groupId} onChange={set("groupId")} required>
                  <option value="" disabled>
                    Select group
                  </option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Category *
                <select value={form.categoryId} onChange={set("categoryId")} required>
                  <option value="" disabled>
                    Select category
                  </option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Roll
                <input type="number" value={form.studentRoll} onChange={set("studentRoll")} />
              </label>
              <label>
                Custom Student ID
                <input
                  value={form.customStudentId}
                  onChange={set("customStudentId")}
                  placeholder="Auto-generated if left blank"
                />
              </label>
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-grid">
            {isEdit && (
              <label>
                Custom Student ID *
                <input value={form.customStudentId} onChange={set("customStudentId")} required />
              </label>
            )}
            <label>
              Student Name *
              <input value={form.studentName} onChange={set("studentName")} required />
            </label>
            <label>
              Student Name (Bangla)
              <input value={form.studentNameBangla} onChange={set("studentNameBangla")} />
            </label>
            <label>
              Nick Name
              <input value={form.studentNickName} onChange={set("studentNickName")} />
            </label>
            <label>
              Gender *
              <select value={form.studentGender} onChange={set("studentGender")} required>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>
            <label>
              Date of Birth
              <input type="date" value={form.studentDob} onChange={set("studentDob")} />
            </label>
            <label>
              Religion *
              <input value={form.studentReligion} onChange={set("studentReligion")} required />
            </label>
            <label>
              Birth Certificate No
              <input value={form.birthCertificateNo} onChange={set("birthCertificateNo")} />
            </label>
            <label>
              Blood Group
              <input value={form.bloodGroup} onChange={set("bloodGroup")} />
            </label>
            <label>
              Registration No
              <input value={form.registrationNo} onChange={set("registrationNo")} />
            </label>
            <label>
              Quota
              <select value={form.quota} onChange={set("quota")}>
                <option value="">Select Quota</option>
                {QUOTA_OPTIONS.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Is Autism
              <select value={form.autism} onChange={set("autism")}>
                {AUTISM_OPTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>Guardian Information</h2>
          <div className="form-grid">
            <label>
              Father's Name *
              <input value={form.fatherName} onChange={set("fatherName")} required />
            </label>
            <label>
              Father's Name (Bangla)
              <input value={form.fatherNameBangla} onChange={set("fatherNameBangla")} />
            </label>
            <label>
              Father's Mobile
              <input value={form.fatherMobile} onChange={set("fatherMobile")} />
            </label>
            <label>
              Father's Occupation
              <select value={form.fatherOccupation} onChange={set("fatherOccupation")}>
                <option value="">Select Occupation</option>
                {FATHER_OCCUPATIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Father's Designation
              <input value={form.fatherDesignation} onChange={set("fatherDesignation")} />
            </label>
            <label>
              Father's Working Place
              <input value={form.fatherWorkingPlace} onChange={set("fatherWorkingPlace")} />
            </label>
            <label>
              Father's Monthly Income
              <input value={form.fatherMonthlyIncome} onChange={set("fatherMonthlyIncome")} />
            </label>
            <label>
              Father's NID
              <input value={form.fatherNid} onChange={set("fatherNid")} />
            </label>
            <label>
              Mother's Name *
              <input value={form.motherName} onChange={set("motherName")} required />
            </label>
            <label>
              Mother's Name (Bangla)
              <input value={form.motherNameBangla} onChange={set("motherNameBangla")} />
            </label>
            <label>
              Mother's Mobile
              <input value={form.motherMobile} onChange={set("motherMobile")} />
            </label>
            <label>
              Mother's Occupation
              <select value={form.motherOccupation} onChange={set("motherOccupation")}>
                <option value="">Select Occupation</option>
                {MOTHER_OCCUPATIONS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Mother's Designation
              <input value={form.motherDesignation} onChange={set("motherDesignation")} />
            </label>
            <label>
              Mother's Working Place
              <input value={form.motherWorkingPlace} onChange={set("motherWorkingPlace")} />
            </label>
            <label>
              Mother's Monthly Income
              <input value={form.motherMonthlyIncome} onChange={set("motherMonthlyIncome")} />
            </label>
            <label>
              Mother's NID
              <input value={form.motherNid} onChange={set("motherNid")} />
            </label>
            <label>
              Guardian Mobile *
              <input value={form.guardianMobile} onChange={set("guardianMobile")} required />
            </label>
            <label>
              Guardian Email
              <input type="email" value={form.guardianEmail} onChange={set("guardianEmail")} />
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>Previous Institution</h2>
          <div className="form-grid">
            <label>
              Institute Name
              <input value={form.previousInstituteName} onChange={set("previousInstituteName")} />
            </label>
            <label>
              EIIN
              <input value={form.previousInstituteCode} onChange={set("previousInstituteCode")} />
            </label>
            <label>
              Address
              <input value={form.previousInstituteAddress} onChange={set("previousInstituteAddress")} />
            </label>
            <label>
              Mobile
              <input value={form.previousInstituteMobile} onChange={set("previousInstituteMobile")} />
            </label>
            <label>
              Email
              <input type="email" value={form.previousInstituteEmail} onChange={set("previousInstituteEmail")} />
            </label>
            <label>
              Exam Name
              <input value={form.previousInstituteExamName} onChange={set("previousInstituteExamName")} />
            </label>
            <label>
              Board Name
              <input value={form.previousInstituteBoardName} onChange={set("previousInstituteBoardName")} />
            </label>
            <label>
              Board Roll
              <input value={form.previousInstituteBoardRoll} onChange={set("previousInstituteBoardRoll")} />
            </label>
            <label>
              Registration No
              <input value={form.previousInstituteRegNo} onChange={set("previousInstituteRegNo")} />
            </label>
            <label>
              GPA
              <input type="number" step="0.01" value={form.previousInstituteGpa} onChange={set("previousInstituteGpa")} />
            </label>
            <label>
              Grade
              <input value={form.previousInstituteGrade} onChange={set("previousInstituteGrade")} />
            </label>
            <label>
              Passing Year
              <input value={form.previousInstitutePassingYear} onChange={set("previousInstitutePassingYear")} />
            </label>
            <label>
              Session
              <input value={form.previousInstituteSession} onChange={set("previousInstituteSession")} />
            </label>
            <label>
              Time Period
              <input value={form.previousInstituteTimePeriod} onChange={set("previousInstituteTimePeriod")} />
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>Address</h2>
          <div className="form-grid">
            <label>
              Mailing Address
              <input value={form.mailingAddress} onChange={set("mailingAddress")} />
            </label>

            <label className="location-field">
              Present Address — Division / District / Thana
              <LocationPicker value={presentLocation} onChange={setPresentLocation} />
            </label>
            <label>
              Present Post Office
              <input value={form.presentPostOffice} onChange={set("presentPostOffice")} />
            </label>
            <label>
              Present Village/Area
              <input value={form.presentVillage} onChange={set("presentVillage")} />
            </label>

            <label className="location-field">
              Permanent Address — Division / District / Thana
              <LocationPicker value={permanentLocation} onChange={setPermanentLocation} />
            </label>
            <label>
              Permanent Post Office
              <input value={form.permanentPostOffice} onChange={set("permanentPostOffice")} />
            </label>
            <label>
              Permanent Village/Area
              <input value={form.permanentVillage} onChange={set("permanentVillage")} />
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Register Student"}
          </button>
        </div>
      </form>
    </MainLayout>
  );
}
